import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, take, tap } from 'rxjs/operators';
import { protectedResources } from '../auth-config';
import {
  AiFeatureRequirement,
  AiSettingsLoadStatus,
  AiOrganizationSettings,
  AiSettingsView,
  AiUserSettings,
  isAiFeatureEnabled,
} from '../ai/ai-settings/ai-settings';

@Injectable({ providedIn: 'root' })
export class AiSettingsService {
  private readonly url = protectedResources.apiBaseUrl.endpoint + '/api/AiSettings';

  private readonly jsonOptions = {
    headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
    withCredentials: true,
  };

  private readonly state$ = new BehaviorSubject<AiSettingsView | null | undefined>(undefined);
  // Why the view is what it is. view$ folds "not allowed" (404) and "failed" (network, 5xx)
  // into null so AI stays hidden, but the settings tab must tell them apart to offer Retry.
  private readonly status$ = new BehaviorSubject<AiSettingsLoadStatus>('loading');
  private loading = false;

  constructor(private http: HttpClient) {}

  view$(): Observable<AiSettingsView | null> {
    this.ensureLoaded();
    return this.state$.pipe(filter((v) => v !== undefined)) as Observable<AiSettingsView | null>;
  }

  isEnabled$(requirement: AiFeatureRequirement): Observable<boolean> {
    return this.view$().pipe(map((v) => isAiFeatureEnabled(v, requirement)), distinctUntilChanged());
  }

  loadStatus$(): Observable<AiSettingsLoadStatus> {
    this.ensureLoaded();
    return this.status$.asObservable();
  }

  loadOnce(): Observable<AiSettingsView | null> {
    return this.view$().pipe(take(1));
  }

  refresh(): void {
    this.loading = false;
    this.state$.next(undefined);
    this.ensureLoaded();
  }

  /**
   * Re-read the settings without the loading flash refresh() causes. Publishes the server's
   * answer (a 404 publishes notAllowed, which hides AI and the tab). On a network or server
   * failure it keeps the current state. Always completes, never errors.
   */
  reload(): Observable<void> {
    return this.http.get<any>(this.url, this.jsonOptions).pipe(
      map((r) => {
        const v = AiSettingsService.toView(r);
        this.publish(v, v ? 'ready' : 'notAllowed');
      }),
      catchError((e: HttpErrorResponse) => {
        if (e?.status === 404) this.publish(null, 'notAllowed');
        return of(undefined);
      })
    );
  }

  updateOrganization(input: AiOrganizationSettings): Observable<AiSettingsView | null> {
    return this.http.put<any>(this.url + '/Organization', input, this.jsonOptions).pipe(
      map((r) => AiSettingsService.toView(r)),
      tap((v) => this.publish(v, v ? 'ready' : 'notAllowed'))
    );
  }

  updateMe(input: AiUserSettings): Observable<AiSettingsView | null> {
    return this.http.put<any>(this.url + '/Me', input, this.jsonOptions).pipe(
      map((r) => AiSettingsService.toView(r)),
      tap((v) => this.publish(v, v ? 'ready' : 'notAllowed'))
    );
  }

  private ensureLoaded(): void {
    if (this.loading || this.state$.value !== undefined) return;
    this.loading = true;
    this.status$.next('loading');
    this.http
      .get<any>(this.url, this.jsonOptions)
      .pipe(
        map((r) => {
          const v = AiSettingsService.toView(r);
          return { v, status: (v ? 'ready' : 'notAllowed') as AiSettingsLoadStatus };
        }),
        catchError((e: HttpErrorResponse) => {
          this.loading = false;
          // The API answers 404 when this user may not see AI at all; anything else is a failure.
          return of({ v: null, status: (e?.status === 404 ? 'notAllowed' : 'error') as AiSettingsLoadStatus });
        })
      )
      .subscribe(({ v, status }) => this.publish(v, status));
  }

  // View first, status last: status is the "settled" signal, so a subscriber reacting to it
  // (SettingComponent's tab visibility) always sees the matching view already applied.
  private publish(v: AiSettingsView | null, status: AiSettingsLoadStatus): void {
    this.state$.next(v);
    this.status$.next(status);
  }

  static toView(r: any): AiSettingsView | null {
    return r && r.result && typeof r.result === 'object' ? (r.result as AiSettingsView) : null;
  }
}
